import React, { useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import { Alert, Button, DatePicker, Popover, Select } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { COLORS, UI_DATE_FORMAT } from "../../../consts";
import { useUser } from "../../../context/UserContext";
import { hexToRgba } from "./utils";

const { RangePicker } = DatePicker;

const toFiniteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const toPositiveInt = (value, fallback) => {
  const numeric = Math.floor(Number(value));
  return Number.isFinite(numeric) && numeric > 0 ? numeric : fallback;
};

const toPositiveNumber = (value, fallback) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : fallback;
};

const getFiniteStats = (values = []) => {
  const finiteValues = values.filter((value) => value !== null);
  if (!finiteValues.length) return { mean: null, std: null };

  const mean =
    finiteValues.reduce((acc, value) => acc + value, 0) / finiteValues.length;
  const variance =
    finiteValues.reduce((acc, value) => acc + (value - mean) ** 2, 0) /
    finiteValues.length;
  const std = Math.sqrt(variance);

  return {
    mean: Number.isFinite(mean) ? mean : null,
    std: Number.isFinite(std) ? std : null,
  };
};

const getRollingWindowStats = (values = [], windowSize = 3, minPoints = 1) =>
  values.map((_, index) => {
    const startIndex = Math.max(0, index - windowSize + 1);
    const slice = values
      .slice(startIndex, index + 1)
      .filter((value) => value !== null);

    if (slice.length < minPoints) {
      return { mean: null, std: null };
    }

    const mean = slice.reduce((acc, value) => acc + value, 0) / slice.length;
    const variance =
      slice.reduce((acc, value) => acc + (value - mean) ** 2, 0) / slice.length;
    const std = Math.sqrt(variance);

    return {
      mean: Number.isFinite(mean) ? mean : null,
      std: Number.isFinite(std) ? std : null,
    };
  });

const buildDeltaSeries = (labels, values, format) => {
  let previousValue = null;
  let previousDate = null;

  return labels.map((label, index) => {
    const currentValue = toFiniteNumber(values[index]);
    const currentDate = dayjs(label, format);
    if (!currentDate.isValid() || currentValue === null) {
      return null;
    }

    if (previousValue === null || !previousDate?.isValid()) {
      previousValue = currentValue;
      previousDate = currentDate;
      return null;
    }

    const dayDifference = Math.max(currentDate.diff(previousDate, "day"), 1);
    const delta = (currentValue - previousValue) / dayDifference;
    previousValue = currentValue;
    previousDate = currentDate;

    return Number.isFinite(delta) ? delta : null;
  });
};

const NdviGraph = ({
  ndviAverageData,
  allRowNdviAverageData,
  siteMetadata,
  visibility,
  setVisibility,
  selectedDates,
  handleDateChange,
  isFullscreen = false,
}) => {
  const GRAPH_UI_DATE_FORMAT = "DD MMM YY";
  const { userData } = useUser();
  const isSuperUser = Boolean(userData?.is_superuser);

  const [graphType, setGraphType] = useState("line");
  const [advancedVisibility, setAdvancedVisibility] = useState({
    ndviStd: true,
    ndviVar: true,
    deltaMean: true,
    deltaMedian: true,
    deltaWidth: true,
    deltaBaseline: true,
    spreadWidth: true,
    lowerTail: true,
    upperTail: true,
    shapeGap: true,
    shapeMeanPosition: true,
    shapeMedianPosition: true,
    shapeCenterBaseline: true,
    cvScore: true,
    snrScore: true,
    rollingRawMean: true,
    rollingShortMean: true,
    rollingLongMean: true,
    zScore: true,
    zZero: true,
    zUpperRef: true,
    zLowerRef: true,
    controlMean: true,
    controlCenter: true,
    controlUcl: true,
    controlLcl: true,
    controlOutliers: true,
  });

  const shortRollingWindow = toPositiveInt(
    siteMetadata?.NDVI_ROLLING_WINDOW_SHORT,
    3,
  );
  const longRollingWindow = Math.max(
    shortRollingWindow,
    toPositiveInt(siteMetadata?.NDVI_ROLLING_WINDOW_LONG, 5),
  );
  const rollingMinPoints = Math.min(
    toPositiveInt(siteMetadata?.NDVI_ROLLING_MIN_POINTS, 1),
    longRollingWindow,
  );
  const zScoreWindow = toPositiveInt(
    siteMetadata?.NDVI_Z_SCORE_WINDOW,
    longRollingWindow,
  );
  const zScoreMinPoints = Math.min(
    toPositiveInt(siteMetadata?.NDVI_Z_SCORE_MIN_POINTS, 3),
    zScoreWindow,
  );
  const zScoreReference = toPositiveNumber(
    siteMetadata?.NDVI_Z_SCORE_REFERENCE,
    2,
  );
  const controlSigma = toPositiveNumber(
    siteMetadata?.NDVI_CONTROL_SIGMA_MULTIPLIER,
    3,
  );

  const dataSource = useMemo(() => {
    if (Array.isArray(ndviAverageData) && ndviAverageData.length > 0) {
      return ndviAverageData;
    }
    if (
      Array.isArray(allRowNdviAverageData) &&
      allRowNdviAverageData.length > 0
    ) {
      return allRowNdviAverageData;
    }
    return [];
  }, [ndviAverageData, allRowNdviAverageData]);

  const dataToUse = useMemo(
    () =>
      [...dataSource].sort((a, b) =>
        dayjs(a.date, GRAPH_UI_DATE_FORMAT).diff(
          dayjs(b.date, GRAPH_UI_DATE_FORMAT),
        ),
      ),
    [dataSource],
  );

  const labels = useMemo(() => dataToUse.map((data) => data.date), [dataToUse]);

  const ndviMeanSeries = useMemo(
    () => dataToUse.map((data) => toFiniteNumber(data.ndviMean)),
    [dataToUse],
  );

  const ndviMedianSeries = useMemo(
    () => dataToUse.map((data) => toFiniteNumber(data.ndviMedian)),
    [dataToUse],
  );

  const ndviStdSeries = useMemo(
    () => dataToUse.map((data) => toFiniteNumber(data.ndviStdDev)),
    [dataToUse],
  );

  const ndviVarSeries = useMemo(
    () => dataToUse.map((data) => toFiniteNumber(data.ndviVar)),
    [dataToUse],
  );

  const ndviMinSeries = useMemo(
    () => dataToUse.map((data) => toFiniteNumber(data.ndviMinValue)),
    [dataToUse],
  );

  const ndviMaxSeries = useMemo(
    () => dataToUse.map((data) => toFiniteNumber(data.ndviMaxValue)),
    [dataToUse],
  );

  const ndviRangeWidthSeries = useMemo(
    () =>
      ndviMaxSeries.map((maxVal, index) => {
        const minVal = ndviMinSeries[index];
        if (maxVal === null || minVal === null) return null;
        return maxVal - minVal;
      }),
    [ndviMaxSeries, ndviMinSeries],
  );

  const ndviLowerTailSeries = useMemo(
    () =>
      ndviMeanSeries.map((meanVal, index) => {
        const minVal = ndviMinSeries[index];
        if (meanVal === null || minVal === null) return null;
        return meanVal - minVal;
      }),
    [ndviMeanSeries, ndviMinSeries],
  );

  const ndviUpperTailSeries = useMemo(
    () =>
      ndviMaxSeries.map((maxVal, index) => {
        const meanVal = ndviMeanSeries[index];
        if (maxVal === null || meanVal === null) return null;
        return maxVal - meanVal;
      }),
    [ndviMaxSeries, ndviMeanSeries],
  );

  const ndviMeanMedianGapSeries = useMemo(
    () =>
      ndviMeanSeries.map((meanVal, index) => {
        const medianVal = ndviMedianSeries[index];
        if (meanVal === null || medianVal === null) return null;
        return meanVal - medianVal;
      }),
    [ndviMeanSeries, ndviMedianSeries],
  );

  const ndviMeanPositionInRangePctSeries = useMemo(
    () =>
      ndviMeanSeries.map((meanVal, index) => {
        const minVal = ndviMinSeries[index];
        const maxVal = ndviMaxSeries[index];
        if (meanVal === null || minVal === null || maxVal === null) return null;
        const denominator = maxVal - minVal;
        if (!Number.isFinite(denominator) || denominator === 0) return null;
        return ((meanVal - minVal) / denominator) * 100;
      }),
    [ndviMeanSeries, ndviMinSeries, ndviMaxSeries],
  );

  const ndviMedianPositionInRangePctSeries = useMemo(
    () =>
      ndviMedianSeries.map((medianVal, index) => {
        const minVal = ndviMinSeries[index];
        const maxVal = ndviMaxSeries[index];
        if (medianVal === null || minVal === null || maxVal === null) return null;
        const denominator = maxVal - minVal;
        if (!Number.isFinite(denominator) || denominator === 0) return null;
        return ((medianVal - minVal) / denominator) * 100;
      }),
    [ndviMedianSeries, ndviMinSeries, ndviMaxSeries],
  );

  const ndviMeanDeltaSeries = useMemo(
    () => buildDeltaSeries(labels, ndviMeanSeries, GRAPH_UI_DATE_FORMAT),
    [labels, ndviMeanSeries],
  );

  const ndviMedianDeltaSeries = useMemo(
    () => buildDeltaSeries(labels, ndviMedianSeries, GRAPH_UI_DATE_FORMAT),
    [labels, ndviMedianSeries],
  );

  const ndviRangeWidthDeltaSeries = useMemo(
    () => buildDeltaSeries(labels, ndviRangeWidthSeries, GRAPH_UI_DATE_FORMAT),
    [labels, ndviRangeWidthSeries],
  );

  const cvSeries = useMemo(
    () =>
      ndviMeanSeries.map((meanValue, index) => {
        const stdValue = ndviStdSeries[index];
        if (meanValue === null || stdValue === null || meanValue === 0) return null;
        return (stdValue / meanValue) * 100;
      }),
    [ndviMeanSeries, ndviStdSeries],
  );

  const snrSeries = useMemo(
    () =>
      ndviMeanSeries.map((meanValue, index) => {
        const stdValue = ndviStdSeries[index];
        if (meanValue === null || stdValue === null || stdValue === 0) return null;
        return meanValue / stdValue;
      }),
    [ndviMeanSeries, ndviStdSeries],
  );

  const rollingShortStats = useMemo(
    () =>
      getRollingWindowStats(
        ndviMeanSeries,
        shortRollingWindow,
        Math.min(rollingMinPoints, shortRollingWindow),
      ),
    [ndviMeanSeries, shortRollingWindow, rollingMinPoints],
  );

  const rollingLongStats = useMemo(
    () =>
      getRollingWindowStats(
        ndviMeanSeries,
        longRollingWindow,
        Math.min(rollingMinPoints, longRollingWindow),
      ),
    [ndviMeanSeries, longRollingWindow, rollingMinPoints],
  );

  const rollingShortMeanSeries = useMemo(
    () => rollingShortStats.map((item) => item.mean),
    [rollingShortStats],
  );

  const rollingLongMeanSeries = useMemo(
    () => rollingLongStats.map((item) => item.mean),
    [rollingLongStats],
  );

  const zRollingStats = useMemo(
    () =>
      getRollingWindowStats(
        ndviMeanSeries,
        zScoreWindow,
        Math.min(zScoreMinPoints, zScoreWindow),
      ),
    [ndviMeanSeries, zScoreWindow, zScoreMinPoints],
  );

  const zScoreSeries = useMemo(
    () =>
      ndviMeanSeries.map((value, index) => {
        const rollingMean = zRollingStats[index]?.mean;
        const rollingStd = zRollingStats[index]?.std;

        if (value === null || rollingMean === null || rollingStd === null) {
          return null;
        }
        if (rollingStd === 0) return null;

        const zValue = (value - rollingMean) / rollingStd;
        return Number.isFinite(zValue) ? zValue : null;
      }),
    [ndviMeanSeries, zRollingStats],
  );

  const globalNdviStats = useMemo(() => getFiniteStats(ndviMeanSeries), [ndviMeanSeries]);

  const controlCenter = globalNdviStats.mean;
  const controlUpper =
    controlCenter === null || globalNdviStats.std === null
      ? null
      : controlCenter + controlSigma * globalNdviStats.std;
  const controlLower =
    controlCenter === null || globalNdviStats.std === null
      ? null
      : controlCenter - controlSigma * globalNdviStats.std;

  const controlOutlierSeries = useMemo(
    () =>
      ndviMeanSeries.map((value) => {
        if (value === null || controlUpper === null || controlLower === null) {
          return null;
        }
        if (value > controlUpper || value < controlLower) {
          return value;
        }
        return null;
      }),
    [ndviMeanSeries, controlUpper, controlLower],
  );

  const getLineVisibility = (key, defaultValue) =>
    visibility?.[key] == null ? defaultValue : Boolean(visibility[key]);

  const toggleLineVisibility = (key, defaultValue) => {
    setVisibility((prevVisibility) => ({
      ...prevVisibility,
      [key]: !(prevVisibility?.[key] == null
        ? defaultValue
        : Boolean(prevVisibility[key])),
    }));
  };

  const toggleAdvancedVisibility = (key) => {
    setAdvancedVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const ndviLineChartData = {
    labels,
    datasets: [
      {
        label: "NDVI Mean",
        data: ndviMeanSeries,
        borderColor: COLORS.primaryBlue,
        backgroundColor: COLORS.primaryBlue,
        fill: false,
        tension: 0.3,
        hidden: !getLineVisibility("NDVI", true),
        spanGaps: true,
      },
      {
        label: "NDVI Median",
        data: ndviMedianSeries,
        borderColor: COLORS.primaryOrange,
        backgroundColor: COLORS.primaryOrange,
        fill: false,
        tension: 0.3,
        hidden: !getLineVisibility("NDVI Median", true),
        spanGaps: true,
      },
      {
        label: "NDVI Max",
        data: ndviMaxSeries,
        borderColor: "#a9cff5",
        backgroundColor: hexToRgba("#a9cff5", 0.1),
        fill: "-1",
        tension: 0.3,
        hidden: !getLineVisibility("NDVI Range", false),
        spanGaps: true,
      },
      {
        label: "NDVI Min",
        data: ndviMinSeries,
        borderColor: "#a9cff5",
        backgroundColor: hexToRgba("#a9cff5", 0.1),
        fill: "-1",
        tension: 0.3,
        hidden: !getLineVisibility("NDVI Range", false),
        spanGaps: true,
      },
    ],
  };

  const ndviVariabilityChartData = {
    labels,
    datasets: [
      {
        label: "NDVI Std Dev",
        data: ndviStdSeries,
        borderColor: COLORS.primaryOrange,
        backgroundColor: COLORS.primaryOrange,
        fill: false,
        tension: 0.25,
        spanGaps: true,
        hidden: !advancedVisibility.ndviStd,
        yAxisID: "y",
      },
      {
        label: "NDVI Variance",
        data: ndviVarSeries,
        borderColor: "#722ed1",
        backgroundColor: "#722ed1",
        fill: false,
        tension: 0.25,
        spanGaps: true,
        hidden: !advancedVisibility.ndviVar,
        yAxisID: "y1",
      },
    ],
  };

  const ndviDeltaChartData = {
    labels,
    datasets: [
      {
        label: "NDVI Mean Delta/day",
        data: ndviMeanDeltaSeries,
        borderColor: COLORS.primaryBlue,
        backgroundColor: COLORS.primaryBlue,
        fill: false,
        tension: 0.25,
        spanGaps: true,
        hidden: !advancedVisibility.deltaMean,
      },
      {
        label: "NDVI Median Delta/day",
        data: ndviMedianDeltaSeries,
        borderColor: COLORS.primaryOrange,
        backgroundColor: COLORS.primaryOrange,
        fill: false,
        tension: 0.25,
        spanGaps: true,
        hidden: !advancedVisibility.deltaMedian,
      },
      {
        label: "Range Width Delta/day",
        data: ndviRangeWidthDeltaSeries,
        borderColor: COLORS.successGreen,
        backgroundColor: COLORS.successGreen,
        fill: false,
        tension: 0.25,
        spanGaps: true,
        hidden: !advancedVisibility.deltaWidth,
      },
      {
        label: "No-Change Baseline",
        data: labels.map(() => 0),
        borderColor: "#8c8c8c",
        backgroundColor: "#8c8c8c",
        fill: false,
        tension: 0,
        borderDash: [6, 4],
        pointRadius: 0,
        spanGaps: true,
        hidden: !advancedVisibility.deltaBaseline,
      },
    ],
  };

  const ndviSpreadChartData = {
    labels,
    datasets: [
      {
        label: "Range Width (Max-Min)",
        data: ndviRangeWidthSeries,
        borderColor: COLORS.successGreen,
        backgroundColor: hexToRgba(COLORS.successGreen, 0.15),
        fill: true,
        tension: 0.25,
        spanGaps: true,
        hidden: !advancedVisibility.spreadWidth,
      },
      {
        label: "Lower Tail (Mean-Min)",
        data: ndviLowerTailSeries,
        borderColor: COLORS.primaryOrange,
        backgroundColor: COLORS.primaryOrange,
        fill: false,
        tension: 0.25,
        spanGaps: true,
        hidden: !advancedVisibility.lowerTail,
      },
      {
        label: "Upper Tail (Max-Mean)",
        data: ndviUpperTailSeries,
        borderColor: "#722ed1",
        backgroundColor: "#722ed1",
        fill: false,
        tension: 0.25,
        spanGaps: true,
        hidden: !advancedVisibility.upperTail,
      },
    ],
  };

  const ndviShapeChartData = {
    labels,
    datasets: [
      {
        label: "Mean-Median Gap",
        data: ndviMeanMedianGapSeries,
        borderColor: COLORS.primaryBlue,
        backgroundColor: COLORS.primaryBlue,
        fill: false,
        tension: 0.25,
        spanGaps: true,
        hidden: !advancedVisibility.shapeGap,
        yAxisID: "y",
      },
      {
        label: "Mean Position in Range (%)",
        data: ndviMeanPositionInRangePctSeries,
        borderColor: COLORS.successGreen,
        backgroundColor: COLORS.successGreen,
        fill: false,
        tension: 0.25,
        spanGaps: true,
        hidden: !advancedVisibility.shapeMeanPosition,
        yAxisID: "y1",
      },
      {
        label: "Median Position in Range (%)",
        data: ndviMedianPositionInRangePctSeries,
        borderColor: COLORS.primaryOrange,
        backgroundColor: COLORS.primaryOrange,
        fill: false,
        tension: 0.25,
        spanGaps: true,
        hidden: !advancedVisibility.shapeMedianPosition,
        yAxisID: "y1",
      },
      {
        label: "Centered Baseline (50%)",
        data: labels.map(() => 50),
        borderColor: "#8c8c8c",
        backgroundColor: "#8c8c8c",
        fill: false,
        tension: 0,
        borderDash: [6, 4],
        pointRadius: 0,
        spanGaps: true,
        hidden: !advancedVisibility.shapeCenterBaseline,
        yAxisID: "y1",
      },
    ],
  };

  const ndviCvChartData = {
    labels,
    datasets: [
      {
        label: "NDVI CV (%)",
        data: cvSeries,
        borderColor: COLORS.primaryBlue,
        backgroundColor: COLORS.primaryBlue,
        fill: false,
        tension: 0.25,
        spanGaps: true,
        hidden: !advancedVisibility.cvScore,
      },
    ],
  };

  const ndviSnrChartData = {
    labels,
    datasets: [
      {
        label: "NDVI Signal-to-Noise (Mean/Std)",
        data: snrSeries,
        borderColor: COLORS.primaryOrange,
        backgroundColor: COLORS.primaryOrange,
        fill: false,
        tension: 0.25,
        spanGaps: true,
        hidden: !advancedVisibility.snrScore,
      },
    ],
  };

  const ndviRollingChartData = {
    labels,
    datasets: [
      {
        label: "NDVI Mean",
        data: ndviMeanSeries,
        borderColor: "#a9cff5",
        backgroundColor: "#a9cff5",
        fill: false,
        tension: 0.2,
        spanGaps: true,
        hidden: !advancedVisibility.rollingRawMean,
      },
      {
        label: `Rolling Mean (${shortRollingWindow}d)`,
        data: rollingShortMeanSeries,
        borderColor: COLORS.primaryBlue,
        backgroundColor: COLORS.primaryBlue,
        fill: false,
        tension: 0.25,
        spanGaps: true,
        hidden: !advancedVisibility.rollingShortMean,
      },
      {
        label: `Rolling Mean (${longRollingWindow}d)`,
        data: rollingLongMeanSeries,
        borderColor: COLORS.successGreen,
        backgroundColor: COLORS.successGreen,
        fill: false,
        tension: 0.25,
        spanGaps: true,
        hidden: !advancedVisibility.rollingLongMean,
      },
    ],
  };

  const ndviZScoreChartData = {
    labels,
    datasets: [
      {
        label: `Rolling Z-Score (${zScoreWindow}d)` ,
        data: zScoreSeries,
        borderColor: COLORS.primaryBlue,
        backgroundColor: COLORS.primaryBlue,
        fill: false,
        tension: 0.25,
        spanGaps: true,
        hidden: !advancedVisibility.zScore,
      },
      {
        label: "Center Baseline (0)",
        data: labels.map(() => 0),
        borderColor: "#8c8c8c",
        backgroundColor: "#8c8c8c",
        fill: false,
        tension: 0,
        borderDash: [6, 4],
        pointRadius: 0,
        spanGaps: true,
        hidden: !advancedVisibility.zZero,
      },
      {
        label: `Upper Ref (+${zScoreReference})`,
        data: labels.map(() => zScoreReference),
        borderColor: COLORS.successGreen,
        backgroundColor: COLORS.successGreen,
        fill: false,
        tension: 0,
        borderDash: [4, 4],
        pointRadius: 0,
        spanGaps: true,
        hidden: !advancedVisibility.zUpperRef,
      },
      {
        label: `Lower Ref (-${zScoreReference})`,
        data: labels.map(() => -zScoreReference),
        borderColor: COLORS.primaryOrange,
        backgroundColor: COLORS.primaryOrange,
        fill: false,
        tension: 0,
        borderDash: [4, 4],
        pointRadius: 0,
        spanGaps: true,
        hidden: !advancedVisibility.zLowerRef,
      },
    ],
  };

  const ndviControlChartData = {
    labels,
    datasets: [
      {
        label: "NDVI Mean",
        data: ndviMeanSeries,
        borderColor: COLORS.primaryBlue,
        backgroundColor: COLORS.primaryBlue,
        fill: false,
        tension: 0.25,
        spanGaps: true,
        hidden: !advancedVisibility.controlMean,
      },
      {
        label: "Control Center (Mean)",
        data: labels.map(() => controlCenter),
        borderColor: "#8c8c8c",
        backgroundColor: "#8c8c8c",
        fill: false,
        tension: 0,
        borderDash: [6, 4],
        pointRadius: 0,
        spanGaps: true,
        hidden: !advancedVisibility.controlCenter,
      },
      {
        label: `UCL (+${controlSigma}σ)`,
        data: labels.map(() => controlUpper),
        borderColor: COLORS.successGreen,
        backgroundColor: COLORS.successGreen,
        fill: false,
        tension: 0,
        borderDash: [4, 4],
        pointRadius: 0,
        spanGaps: true,
        hidden: !advancedVisibility.controlUcl,
      },
      {
        label: `LCL (-${controlSigma}σ)`,
        data: labels.map(() => controlLower),
        borderColor: COLORS.primaryOrange,
        backgroundColor: COLORS.primaryOrange,
        fill: false,
        tension: 0,
        borderDash: [4, 4],
        pointRadius: 0,
        spanGaps: true,
        hidden: !advancedVisibility.controlLcl,
      },
      {
        label: "Out-of-Control Points",
        data: controlOutlierSeries,
        borderColor: "#cf1322",
        backgroundColor: "#cf1322",
        showLine: false,
        pointRadius: 4,
        pointHoverRadius: 5,
        spanGaps: false,
        hidden: !advancedVisibility.controlOutliers,
      },
    ],
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            return value === null
              ? `${tooltipItem.dataset.label}: N/A`
              : `${tooltipItem.dataset.label}: ${Number(value).toFixed(4)}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: { display: true, text: "NDVI", font: { size: 16 } },
        grid: { display: false },
        ticks: { font: { size: 14 } },
        min: 0,
        max: 1,
      },
    },
  };

  const variabilityOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            return value === null
              ? `${tooltipItem.dataset.label}: N/A`
              : `${tooltipItem.dataset.label}: ${Number(value).toFixed(6)}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: { display: true, text: "NDVI Std Dev", font: { size: 16 } },
        grid: { display: false },
        ticks: { font: { size: 14 } },
      },
      y1: {
        position: "right",
        title: { display: true, text: "NDVI Variance", font: { size: 16 } },
        grid: { display: false, drawOnChartArea: false },
        ticks: { font: { size: 14 } },
      },
    },
  };

  const deltaOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            return value === null
              ? `${tooltipItem.dataset.label}: N/A`
              : `${tooltipItem.dataset.label}: ${Number(value).toFixed(5)} /day`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: { display: true, text: "Day-over-day NDVI Delta", font: { size: 16 } },
        grid: { display: false },
        ticks: { font: { size: 14 } },
      },
    },
  };

  const spreadOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            return value === null
              ? `${tooltipItem.dataset.label}: N/A`
              : `${tooltipItem.dataset.label}: ${Number(value).toFixed(5)}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: { display: true, text: "NDVI Spread Components", font: { size: 16 } },
        grid: { display: false },
        ticks: { font: { size: 14 } },
      },
    },
  };

  const shapeOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            if (value === null) return `${tooltipItem.dataset.label}: N/A`;
            if (tooltipItem.dataset.yAxisID === "y1") {
              return `${tooltipItem.dataset.label}: ${Number(value).toFixed(2)}%`;
            }
            return `${tooltipItem.dataset.label}: ${Number(value).toFixed(5)}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: { display: true, text: "Mean-Median Gap", font: { size: 16 } },
        grid: { display: false },
        ticks: { font: { size: 14 } },
      },
      y1: {
        position: "right",
        title: { display: true, text: "Position in Range (%)", font: { size: 16 } },
        grid: { display: false, drawOnChartArea: false },
        ticks: { font: { size: 14 } },
        min: 0,
        max: 100,
      },
    },
  };

  const cvOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            return value === null
              ? `${tooltipItem.dataset.label}: N/A`
              : `${tooltipItem.dataset.label}: ${Number(value).toFixed(3)}%`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: { display: true, text: "CV (%)", font: { size: 16 } },
        grid: { display: false },
        ticks: {
          font: { size: 14 },
          callback: (value) => `${value}%`,
        },
      },
    },
  };

  const snrOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            return value === null
              ? `${tooltipItem.dataset.label}: N/A`
              : `${tooltipItem.dataset.label}: ${Number(value).toFixed(3)}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: { display: true, text: "Signal-to-Noise", font: { size: 16 } },
        grid: { display: false },
        ticks: { font: { size: 14 } },
      },
    },
  };

  const rollingOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            return value === null
              ? `${tooltipItem.dataset.label}: N/A`
              : `${tooltipItem.dataset.label}: ${Number(value).toFixed(4)}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: { display: true, text: "NDVI", font: { size: 16 } },
        grid: { display: false },
        ticks: { font: { size: 14 } },
        min: 0,
        max: 1,
      },
    },
  };

  const zScoreOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            return value === null
              ? `${tooltipItem.dataset.label}: N/A`
              : `${tooltipItem.dataset.label}: ${Number(value).toFixed(3)}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: { display: true, text: "Rolling Z-Score", font: { size: 16 } },
        grid: { display: false },
        ticks: { font: { size: 14 } },
      },
    },
  };

  const controlOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            return value === null
              ? `${tooltipItem.dataset.label}: N/A`
              : `${tooltipItem.dataset.label}: ${Number(value).toFixed(4)}`;
          },
          afterBody: function () {
            if (
              controlCenter === null ||
              controlUpper === null ||
              controlLower === null
            ) {
              return [];
            }
            return [
              `Control Center: ${controlCenter.toFixed(4)}`,
              `UCL: ${controlUpper.toFixed(4)}`,
              `LCL: ${controlLower.toFixed(4)}`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: { display: true, text: "NDVI", font: { size: 16 } },
        grid: { display: false },
        ticks: { font: { size: 14 } },
        min: 0,
        max: 1,
      },
    },
  };

  const graphTypeOptions = [
    { label: "Line", value: "line" },
    { label: "Variability", value: "variability" },
    { label: "Delta", value: "delta" },
    { label: "Spread", value: "spread" },
    { label: "Shape", value: "shape" },
    { label: "CV %", value: "cv" },
    { label: "Signal-to-Noise", value: "snr" },
    { label: "Rolling Mean", value: "rolling" },
    { label: "Z-Score", value: "zscore" },
    { label: "Control Bands", value: "control" },
  ];

  const graphInfoByType = {
    line: {
      short:
        "NDVI Trend: Tracks central NDVI behavior with median and full min-max range over time.",
      relevance:
        "Best for daily crop vigor trend checks and outlier-bound monitoring.",
      details: [
        "NDVI Mean uses: ndvi_mean.",
        "NDVI Median uses: ndvi_median.",
        "NDVI Range band uses: ndvi_minval to ndvi_maxval.",
      ],
    },
    variability: {
      short:
        "NDVI Variability: Shows how spread/dispersion changes over time.",
      relevance:
        "Best for checking uniformity consistency and variability drift.",
      details: [
        "Standard deviation uses: ndvi_std.",
        "Variance uses: ndvi_var.",
      ],
    },
    delta: {
      short:
        "NDVI Delta: Shows day-over-day movement for NDVI mean/median and width.",
      relevance:
        "Best for detecting acceleration, slowdown, or sudden shifts.",
      details: [
        "Mean delta/day = (current ndvi_mean - previous ndvi_mean) / day gap.",
        "Median delta/day = (current ndvi_median - previous ndvi_median) / day gap.",
        "Width delta/day = (current (ndvi_maxval-ndvi_minval) - previous width) / day gap.",
      ],
    },
    spread: {
      short:
        "NDVI Spread Components: Breaks total range into lower and upper tail components.",
      relevance:
        "Best for understanding if variability is growing from low-end or high-end tail.",
      details: [
        "Range width = ndvi_maxval - ndvi_minval.",
        "Lower tail = ndvi_mean - ndvi_minval.",
        "Upper tail = ndvi_maxval - ndvi_mean.",
      ],
    },
    shape: {
      short:
        "NDVI Shape: Tracks asymmetry and center position within each day's NDVI range.",
      relevance:
        "Best for detecting left/right skew shifts and where central tendency sits in range.",
      details: [
        "Mean-median gap = ndvi_mean - ndvi_median.",
        "Mean position (%) = ((ndvi_mean - ndvi_minval) / (ndvi_maxval - ndvi_minval)) * 100.",
        "Median position (%) = ((ndvi_median - ndvi_minval) / (ndvi_maxval - ndvi_minval)) * 100.",
      ],
    },
    cv: {
      short:
        "CV Trend: Normalized variability as a percentage of mean NDVI.",
      relevance:
        "Best for comparing variability across dates on a normalized scale.",
      details: [
        "CV (%) = (ndvi_std / ndvi_mean) * 100.",
      ],
    },
    snr: {
      short:
        "Signal-to-Noise: Mean NDVI strength relative to NDVI variability.",
      relevance:
        "Best for tracking stability quality of NDVI signal over time.",
      details: [
        "SNR = ndvi_mean / ndvi_std.",
      ],
    },
    rolling: {
      short:
        "Rolling Mean: Smoothed NDVI trend using short and long rolling windows.",
      relevance:
        "Best for reducing noise and seeing directional NDVI movement.",
      details: [
        `Rolling short window uses ${shortRollingWindow} days (or NDVI_ROLLING_WINDOW_SHORT).`,
        `Rolling long window uses ${longRollingWindow} days (or NDVI_ROLLING_WINDOW_LONG).`,
      ],
    },
    zscore: {
      short:
        "Z-Score Trend: Standardized NDVI deviation from rolling mean behavior.",
      relevance:
        "Best for flagging unusually high/low NDVI compared to recent history.",
      details: [
        `Rolling z-score = (ndvi_mean - rolling_mean_${zScoreWindow}d) / rolling_std_${zScoreWindow}d.`,
        `Reference lines use +/-${zScoreReference}.`,
      ],
    },
    control: {
      short:
        "Control Bands: NDVI mean with centerline and sigma-based control limits.",
      relevance:
        "Best for spotting statistically unusual NDVI points at a glance.",
      details: [
        "Control center = global mean of ndvi_mean across selected dates.",
        `UCL/LCL = center +/- (${controlSigma} * global std of ndvi_mean).`,
        "Out-of-control points are values outside UCL/LCL.",
      ],
    },
  };

  const lineLegendItems = [
    {
      key: "NDVI",
      label: "NDVI Mean",
      color: COLORS.primaryBlue,
      defaultVisible: true,
    },
    {
      key: "NDVI Median",
      label: "NDVI Median",
      color: COLORS.primaryOrange,
      defaultVisible: true,
    },
    {
      key: "NDVI Range",
      label: "NDVI Range (Min-Max)",
      color: "#a9cff5",
      defaultVisible: false,
    },
  ];

  const advancedLegendByType = {
    variability: [
      { key: "ndviStd", label: "NDVI Std Dev", color: COLORS.primaryOrange },
      { key: "ndviVar", label: "NDVI Variance", color: "#722ed1" },
    ],
    delta: [
      { key: "deltaMean", label: "Mean Delta/day", color: COLORS.primaryBlue },
      {
        key: "deltaMedian",
        label: "Median Delta/day",
        color: COLORS.primaryOrange,
      },
      { key: "deltaWidth", label: "Width Delta/day", color: COLORS.successGreen },
      { key: "deltaBaseline", label: "No-Change Baseline", color: "#8c8c8c" },
    ],
    spread: [
      {
        key: "spreadWidth",
        label: "Range Width (Max-Min)",
        color: COLORS.successGreen,
      },
      {
        key: "lowerTail",
        label: "Lower Tail (Mean-Min)",
        color: COLORS.primaryOrange,
      },
      { key: "upperTail", label: "Upper Tail (Max-Mean)", color: "#722ed1" },
    ],
    shape: [
      { key: "shapeGap", label: "Mean-Median Gap", color: COLORS.primaryBlue },
      {
        key: "shapeMeanPosition",
        label: "Mean Position (%)",
        color: COLORS.successGreen,
      },
      {
        key: "shapeMedianPosition",
        label: "Median Position (%)",
        color: COLORS.primaryOrange,
      },
      {
        key: "shapeCenterBaseline",
        label: "Centered Baseline (50%)",
        color: "#8c8c8c",
      },
    ],
    cv: [{ key: "cvScore", label: "NDVI CV (%)", color: COLORS.primaryBlue }],
    snr: [
      {
        key: "snrScore",
        label: "Signal-to-Noise",
        color: COLORS.primaryOrange,
      },
    ],
    rolling: [
      { key: "rollingRawMean", label: "NDVI Mean", color: "#a9cff5" },
      {
        key: "rollingShortMean",
        label: `Rolling Mean (${shortRollingWindow}d)`,
        color: COLORS.primaryBlue,
      },
      {
        key: "rollingLongMean",
        label: `Rolling Mean (${longRollingWindow}d)`,
        color: COLORS.successGreen,
      },
    ],
    zscore: [
      {
        key: "zScore",
        label: `Rolling Z-Score (${zScoreWindow}d)`,
        color: COLORS.primaryBlue,
      },
      { key: "zZero", label: "Center Baseline (0)", color: "#8c8c8c" },
      {
        key: "zUpperRef",
        label: `Upper Ref (+${zScoreReference})`,
        color: COLORS.successGreen,
      },
      {
        key: "zLowerRef",
        label: `Lower Ref (-${zScoreReference})`,
        color: COLORS.primaryOrange,
      },
    ],
    control: [
      { key: "controlMean", label: "NDVI Mean", color: COLORS.primaryBlue },
      {
        key: "controlCenter",
        label: "Control Center",
        color: "#8c8c8c",
      },
      {
        key: "controlUcl",
        label: `UCL (+${controlSigma}σ)`,
        color: COLORS.successGreen,
      },
      {
        key: "controlLcl",
        label: `LCL (-${controlSigma}σ)`,
        color: COLORS.primaryOrange,
      },
      {
        key: "controlOutliers",
        label: "Out-of-Control Points",
        color: "#cf1322",
      },
    ],
  };

  const effectiveGraphType = isSuperUser ? graphType : "line";

  const activeChartData =
    effectiveGraphType === "variability"
      ? ndviVariabilityChartData
      : effectiveGraphType === "delta"
      ? ndviDeltaChartData
      : effectiveGraphType === "spread"
      ? ndviSpreadChartData
      : effectiveGraphType === "shape"
      ? ndviShapeChartData
      : effectiveGraphType === "cv"
      ? ndviCvChartData
      : effectiveGraphType === "snr"
      ? ndviSnrChartData
      : effectiveGraphType === "rolling"
      ? ndviRollingChartData
      : effectiveGraphType === "zscore"
      ? ndviZScoreChartData
      : effectiveGraphType === "control"
      ? ndviControlChartData
      : ndviLineChartData;

  const activeChartOptions =
    effectiveGraphType === "variability"
      ? variabilityOptions
      : effectiveGraphType === "delta"
      ? deltaOptions
      : effectiveGraphType === "spread"
      ? spreadOptions
      : effectiveGraphType === "shape"
      ? shapeOptions
      : effectiveGraphType === "cv"
      ? cvOptions
      : effectiveGraphType === "snr"
      ? snrOptions
      : effectiveGraphType === "rolling"
      ? rollingOptions
      : effectiveGraphType === "zscore"
      ? zScoreOptions
      : effectiveGraphType === "control"
      ? controlOptions
      : lineOptions;

  const activeGraphInfo =
    graphInfoByType[effectiveGraphType] || graphInfoByType.line;

  const isDefaultLineType = effectiveGraphType === "line";
  const activeAdvancedLegend = advancedLegendByType[effectiveGraphType] || [];

  return (
    <div className={`flex w-full flex-col ${isFullscreen ? "h-full min-h-0" : ""}`}>
      <h2 className={`font-bold ${isFullscreen ? "text-xl" : "text-2xl"}`}>
        NDVI Trend
      </h2>
      <div
        className={`graph-header w-full flex flex-wrap justify-between items-center sm:flex-col ${
          isFullscreen ? "mb-2 gap-2" : "mb-4"
        }`}
      >
        {isDefaultLineType ? (
          <div className="custom-legend flex flex-wrap gap-3 sm:flex-col">
            {lineLegendItems.map((item) => {
              const itemVisible = getLineVisibility(item.key, item.defaultVisible);
              return (
                <div
                  key={item.key}
                  className="flex items-center mb-1 cursor-pointer"
                  onClick={() => toggleLineVisibility(item.key, item.defaultVisible)}
                >
                  <div
                    className="relative w-10 h-0.5 mr-2"
                    style={{
                      backgroundColor: item.color,
                      opacity: itemVisible ? 1 : 0.3,
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: item.color,
                        opacity: itemVisible ? 1 : 0.3,
                      }}
                      className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full"
                    ></div>
                  </div>
                  <span style={{ opacity: itemVisible ? 1 : 0.3 }}>{item.label}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="custom-legend flex flex-wrap gap-3 sm:flex-col">
            {activeAdvancedLegend.map((item) => (
              <div
                key={item.key}
                className="flex items-center mb-1 cursor-pointer"
                onClick={() => toggleAdvancedVisibility(item.key)}
              >
                <div
                  className="relative w-10 h-0.5 mr-2"
                  style={{
                    backgroundColor: item.color,
                    opacity: advancedVisibility[item.key] ? 1 : 0.3,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: item.color,
                      opacity: advancedVisibility[item.key] ? 1 : 0.3,
                    }}
                    className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full"
                  ></div>
                </div>
                <span style={{ opacity: advancedVisibility[item.key] ? 1 : 0.3 }}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        )}

        <div
          className={`flex items-center gap-2 ${
            isFullscreen
              ? "w-full justify-end sm:w-auto sm:justify-start"
              : "ml-auto"
          }`}
        >
          {isSuperUser && (
            <>
              <span
                className="text-xs font-semibold"
                style={{ color: COLORS.darkerGray }}
              >
                Graph Type:
              </span>
              <Select
                value={graphType}
                onChange={setGraphType}
                options={graphTypeOptions}
                className="w-[210px]"
              />
            </>
          )}
          <RangePicker
            defaultValue={selectedDates}
            value={selectedDates}
            onChange={handleDateChange}
            className="date flex items-center justify-center"
            format={UI_DATE_FORMAT}
          />
        </div>
      </div>

      {isSuperUser && (
        <Alert
          type="info"
          showIcon={false}
          className={`${isFullscreen ? "mb-2" : "mb-3"} !py-1`}
          style={{ border: "none", backgroundColor: "transparent" }}
          message={
            <div className="text-xs leading-5" style={{ color: COLORS.primaryBlue }}>
              <span className="font-semibold">{activeGraphInfo.short}</span>{" "}
              <span>{activeGraphInfo.relevance}</span>
              <Popover
                trigger="click"
                placement="bottomRight"
                title="How this graph is calculated"
                content={
                  <div className="max-w-[380px] text-xs leading-5">
                    <div className="mb-1 font-semibold">{activeGraphInfo.relevance}</div>
                    {activeGraphInfo.details.map((detail) => (
                      <div key={detail}>{detail}</div>
                    ))}
                  </div>
                }
              >
                <Button
                  type="text"
                  size="small"
                  aria-label="Graph calculation details"
                  icon={<InfoCircleOutlined style={{ color: COLORS.primaryBlue }} />}
                  className="!ml-1 !h-auto !w-auto !min-w-0 !p-0 align-middle"
                />
              </Popover>
            </div>
          }
        />
      )}

      <div
        className={`w-full ${
          isFullscreen
            ? "min-h-0 flex-1 overflow-x-auto overflow-y-hidden"
            : "overflow-x-auto"
        }`}
      >
        <div className={isFullscreen ? "h-full min-w-[760px]" : "min-w-[640px]"}>
          <Line data={activeChartData} options={activeChartOptions} />
        </div>
      </div>
    </div>
  );
};

export { NdviGraph };
